import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import {
  exportToCSV, 
  exportToJSON, 
  exportToExcel, 
  exportToPDF,
  formatForExport,
  getExportFilename 
} from '../../utils/exportUtils';
import { shouldShowApiToast } from '../../utils/helpers';
import {
  getTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  duplicateTemplate,
  clearApiMessage,
  clearError,
  selectTemplates,
  selectBroadcastsLoading,
  selectBroadcastsApiMessage,
  selectBroadcastsError,
} from '../../store/slices/main/broadcastSlice';
import { getMembers, selectMembers } from '../../store/slices/main/memberSlice';
import { selectCurrentOrganization } from '../../store/slices/authSlice';
import { getMergeFieldValue } from '../../utils/mergeFieldValues';
import { Download, BarChart3, ClipboardList, TrendingUp, FileText, FilePlus2, Pencil, Trash2, Lightbulb, Save, AlertTriangle, Folder, Search, Copy, Loader2 } from 'lucide-react';

const TemplatesLibrary = ({ showToast }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();
  
  // ==================== STATE ====================
  const [activeCategory, setActiveCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);

  // ==================== REFS ====================
  const editNameRef = useRef(null);
  const editCategoryRef = useRef(null);
  const editBodyRef = useRef(null);
  const createNameRef = useRef(null);
  const createCategoryRef = useRef(null);
  const createBodyRef = useRef(null);

  // Track last toast message to prevent duplicates
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== SELECTORS ====================
  const templates = useSelector(selectTemplates) || [];
  const loading = useSelector(selectBroadcastsLoading);
  const apiMessage = useSelector(selectBroadcastsApiMessage);
  const error = useSelector(selectBroadcastsError);
  const members = useSelector(selectMembers) || [];
  const organization = useSelector(selectCurrentOrganization);

  // ==================== MERGE FIELDS ====================
  const baseMergeFields = [
    { 
      key: '{FirstName}', 
      label: 'First Name', 
      description: "Member's first name", 
      example: 'John',
      category: 'Personal'
    },
    { 
      key: '{LastName}', 
      label: 'Last Name', 
      description: "Member's last name", 
      example: 'Doe',
      category: 'Personal'
    },
    { 
      key: '{FullName}', 
      label: 'Full Name', 
      description: "Member's full name", 
      example: 'John Doe',
      category: 'Personal'
    },
    { 
      key: '{Phone}', 
      label: 'Phone Number', 
      description: "Member's phone number", 
      example: '+233 24 412 3456',
      category: 'Personal'
    },
    { 
      key: '{Email}', 
      label: 'Email Address', 
      description: "Member's email address", 
      example: 'john@example.com',
      category: 'Personal'
    },
    { 
      key: '{ChurchName}', 
      label: 'Church Name', 
      description: 'Your church name', 
      example: 'Grace Chapel',
      category: 'Church'
    },
    { 
      key: '{PastorName}', 
      label: 'Pastor Name', 
      description: "Pastor's name", 
      example: 'Pastor Michael',
      category: 'Church'
    },
    { 
      key: '{ChurchAddress}', 
      label: 'Church Address', 
      description: 'Church address', 
      example: '123 Main St, Accra',
      category: 'Church'
    },
    { 
      key: '{Group}', 
      label: 'Group', 
      description: "Member's group", 
      example: 'Youth Ministry',
      category: 'Membership'
    },
    { 
      key: '{Branch}', 
      label: 'Branch', 
      description: "Member's branch", 
      example: 'Main Campus',
      category: 'Membership'
    },
    { 
      key: '{Date}', 
      label: 'Current Date', 
      description: 'Current date', 
      example: 'Sunday, July 14, 2026',
      category: 'System'
    },
    { 
      key: '{Time}', 
      label: 'Current Time', 
      description: 'Current time', 
      example: '10:00 AM',
      category: 'System'
    },
    { 
      key: '{EventName}', 
      label: 'Event Name', 
      description: 'Event or service name', 
      example: 'Sunday Service',
      category: 'Event'
    },
    { 
      key: '{EventDate}', 
      label: 'Event Date', 
      description: 'Date of the event', 
      example: 'July 21, 2026',
      category: 'Event'
    },
    { 
      key: '{EventTime}', 
      label: 'Event Time', 
      description: 'Time of the event', 
      example: '10:00 AM',
      category: 'Event'
    },
    { 
      key: '{EventLocation}', 
      label: 'Event Location', 
      description: 'Location of the event', 
      example: 'Main Auditorium',
      category: 'Event'
    },
  ];
  const mergeFields = baseMergeFields.map((field) => ({
    ...field,
    example: getMergeFieldValue(field.key, { member: members[0], organization })
  }));

  // ==================== HELPER FUNCTIONS ====================
  
  // Insert merge field at cursor position
  const insertMergeField = (field, textareaRef) => {
    const textarea = textareaRef?.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    
    textarea.value = value.substring(0, start) + field + value.substring(end);
    textarea.focus();
    textarea.selectionStart = textarea.selectionEnd = start + field.length;
    textarea.dispatchEvent(new Event('input'));
  };

  // Get merge fields by category
  const getMergeFieldsByCategory = (category) => {
    return mergeFields.filter(f => f.category === category);
  };

  // Get all unique categories
  const mergeFieldCategories = [...new Set(mergeFields.map(f => f.category))];

  // ==================== EFFECTS ====================
  
  // Load templates on mount
  useEffect(() => {
    dispatch(getTemplates());
    dispatch(getMembers({ per_page: 1 }));
  }, [dispatch]);

  // Display API messages
  useEffect(() => {
    if (!apiMessage || !showToast || !shouldShowApiToast(apiMessage, !!error)) return;
    
    const messageKey = `${apiMessage}_${error ? 'error' : 'success'}`;
    const now = Date.now();
    
    if (lastToastRef.current.message === messageKey && 
        (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }
    
    lastToastRef.current = { message: messageKey, timestamp: now };
    
    const toastType = error ? 'error' : 'success';
    showToast(apiMessage, toastType);
    
    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  // ==================== HELPERS ====================

  // Get unique categories from templates
  const categories = [...new Set(templates.map(t => t.type || t.category || 'Uncategorized'))];
  
  const catColors = {
    'Birthday': 'var(--gold)',
    'Service': 'var(--blue)',
    'Welcome': 'var(--green)',
    'Pastoral Care': 'var(--purple)',
    'Events': 'var(--amber)',
    'Giving': 'var(--green)',
    'Prayer': 'var(--navy)',
    'Milestone': 'var(--gold)',
    'Poll': 'var(--blue)',
    'Alert': 'var(--red)',
    'custom': 'var(--text-mid)',
    'Uncategorized': 'var(--text-light)'
  };

  // Filter templates
  const filteredTemplates = templates.filter(t => {
    const matchesCategory = !activeCategory || (t.type || t.category || 'Uncategorized') === activeCategory;
    const matchesSearch = (t.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (t.text || t.body || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (t.type || t.category || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Pagination calculations
  const totalItems = filteredTemplates.length;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTemplates.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  const filterTemplates = (cat) => {
    setActiveCategory(cat);
    setCurrentPage(1);
  };

  const getCategoryColor = (category) => {
    return catColors[category] || catColors['custom'] || 'var(--text-mid)';
  };

  // ==================== EXPORT FUNCTIONS ====================

  const openExportModal = (exportType = 'all', template = null) => {
    let data = [];
    let title = 'Templates Report';
    let filename = 'templates-report';

    if (exportType === 'single' && template) {
      data = [{
        'Template Name': template.name,
        'Category': template.type || template.category || 'Uncategorized',
        'Message Body': template.text || template.body || '',
        'Times Used': template.uses || 0,
        'Merge Fields': ((template.text || template.body || '').match(/\{[A-Za-z]+\}/g) || []).join(', ')
      }];
      title = `Template: ${template.name}`;
      filename = `template-${template.id || 'unknown'}-${(template.name || 'template').substring(0, 30)}`;
    } else if (exportType === 'category' && activeCategory) {
      data = templates
        .filter(t => (t.type || t.category || 'Uncategorized') === activeCategory)
        .map(t => ({
          'Template Name': t.name || 'Unnamed',
          'Category': t.type || t.category || 'Uncategorized',
          'Message Body': t.text || t.body || '',
          'Times Used': t.uses || 0,
          'Merge Fields': ((t.text || t.body || '').match(/\{[A-Za-z]+\}/g) || []).join(', ')
        }));
      title = `Templates: ${activeCategory}`;
      filename = `templates-${activeCategory.toLowerCase()}`;
    } else if (exportType === 'most-used') {
      const sorted = [...templates].sort((a, b) => (b.uses || 0) - (a.uses || 0));
      data = sorted.slice(0, 10).map(t => ({
        'Template Name': t.name || 'Unnamed',
        'Category': t.type || t.category || 'Uncategorized',
        'Times Used': t.uses || 0,
        'Message Body': ((t.text || t.body || '').substring(0, 100)) + ((t.text || t.body || '').length > 100 ? '...' : '')
      }));
      title = 'Most Used Templates';
      filename = 'most-used-templates';
    } else if (exportType === 'summary') {
      const categoryCounts = categories.map(c => ({
        category: c,
        count: templates.filter(t => (t.type || t.category || 'Uncategorized') === c).length,
        totalUses: templates.filter(t => (t.type || t.category || 'Uncategorized') === c).reduce((sum, t) => sum + (t.uses || 0), 0)
      }));
      
      data = [
        { 'Metric': 'Total Templates', 'Value': templates.length },
        { 'Metric': 'Categories', 'Value': categories.length },
        ...categoryCounts.map(cc => ({ 
          'Metric': `${cc.category} Templates`, 
          'Value': `${cc.count} (${cc.totalUses} uses)` 
        })),
        ...(templates.length > 0 ? [
          { 'Metric': 'Most Used Template', 'Value': templates.reduce((a, b) => (a.uses || 0) > (b.uses || 0) ? a : b).name || 'N/A' },
          { 'Metric': 'Total Template Uses', 'Value': templates.reduce((sum, t) => sum + (t.uses || 0), 0) }
        ] : [])
      ];
      title = 'Templates Summary';
      filename = 'templates-summary';
    } else {
      data = filteredTemplates.length > 0 ? filteredTemplates : templates;
      data = data.map(t => ({
        'Template ID': t.id || 'N/A',
        'Template Name': t.name || 'Unnamed',
        'Category': t.type || t.category || 'Uncategorized',
        'Message Body': t.text || t.body || '',
        'Times Used': t.uses || 0,
        'Merge Fields': ((t.text || t.body || '').match(/\{[A-Za-z]+\}/g) || []).join(', ')
      }));
      title = 'All Templates';
      filename = 'all-templates';
    }

    if (data.length === 0) {
      showToast('No data to export', 'error');
      return;
    }

    const fields = Object.keys(data[0]).map(key => ({ key, label: key }));

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Download size={16} style={{ verticalAlign: '-3px' }} /> Export {title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Export {data.length} record{data.length !== 1 ? 's' : ''} in your preferred format.
            {exportType === 'single' && template && (
              <span style={{ marginLeft: '8px', fontWeight: '600', color: 'var(--navy)' }}>
                · {template.name}
              </span>
            )}
            {exportType === 'category' && activeCategory && (
              <span style={{ marginLeft: '8px', fontWeight: '600', color: 'var(--navy)' }}>
                · {activeCategory} category
              </span>
            )}
            {exportType === 'most-used' && (
              <span style={{ marginLeft: '8px', fontWeight: '600', color: 'var(--gold)' }}>
                · Top 10 most used
              </span>
            )}
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Select Format
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    let exportData = data;
                    
                    if (exportType === 'summary') {
                      exportData = data;
                    }
                    
                    switch (format) {
                      case 'CSV':
                        exportToCSV(exportData, getExportFilename(filename));
                        break;
                      case 'JSON':
                        exportToJSON(exportData, getExportFilename(filename));
                        break;
                      case 'Excel':
                        exportToExcel(exportData, getExportFilename(filename));
                        break;
                      case 'PDF':
                        exportToPDF(exportData, title, getExportFilename(filename));
                        break;
                      default:
                        showToast('Export format not supported', 'error');
                        return;
                    }
                    showToast(`📥 ${data.length} records exported as ${format}!`, 'success');
                    closeModal();
                  }}
                  style={{ 
                    padding: '8px', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: 'auto',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--gold)';
                    e.currentTarget.style.background = 'var(--gold-pale)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <span style={{ fontSize: '20px' }}>
                    {format === 'CSV' && <BarChart3 size={20} />}
                    {format === 'JSON' && <ClipboardList size={20} />}
                    {format === 'Excel' && <TrendingUp size={20} />}
                    {format === 'PDF' && <FileText size={20} />}
                  </span>
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{
            padding: '10px 12px',
            background: 'var(--cream)',
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            <span><BarChart3 size={14} style={{ verticalAlign: '-2px' }} /> {data.length} records will be exported</span>
            <span style={{ fontSize: '11px' }}>{title}</span>
          </div>

          {/* Quick Export Options */}
          <div style={{ marginTop: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Quick Export Options
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button 
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  const byCategory = categories.map(c => ({
                    'Category': c,
                    'Count': templates.filter(t => (t.type || t.category || 'Uncategorized') === c).length,
                    'Total Uses': templates.filter(t => (t.type || t.category || 'Uncategorized') === c).reduce((sum, t) => sum + (t.uses || 0), 0)
                  }));
                  exportToCSV(byCategory, getExportFilename('templates-by-category'));
                  showToast(`📥 Templates by category exported as CSV!`, 'success');
                  closeModal();
                }}
              >
                <BarChart3 size={16} style={{ verticalAlign: '-3px' }} /> By Category
              </button>
              <button 
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  const usedData = templates
                    .filter(t => (t.uses || 0) > 0)
                    .sort((a, b) => (b.uses || 0) - (a.uses || 0))
                    .map(t => ({
                      'Template': t.name || 'Unnamed',
                      'Category': t.type || t.category || 'Uncategorized',
                      'Times Used': t.uses || 0
                    }));
                  if (usedData.length === 0) {
                    showToast('No templates have been used yet', 'info');
                    closeModal();
                    return;
                  }
                  exportToCSV(usedData, getExportFilename('used-templates'));
                  showToast(`📥 ${usedData.length} used templates exported as CSV!`, 'success');
                  closeModal();
                }}
              >
                <TrendingUp size={16} style={{ verticalAlign: '-3px' }} /> Used Only
              </button>
              {template && (
                <button 
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    const data = [{
                      'Template': template.name,
                      'Category': template.type || template.category || 'Uncategorized',
                      'Body': template.text || template.body || '',
                      'Uses': template.uses || 0
                    }];
                    exportToCSV(data, getExportFilename(`template-${template.id}`));
                    showToast(`📥 "${template.name}" exported as CSV!`, 'success');
                    closeModal();
                  }}
                >
                  <FileText size={16} style={{ verticalAlign: '-3px' }} /> This Template
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  // Export handlers
  const handleExportAll = () => {
    if (templates.length === 0) {
      showToast('No templates to export', 'error');
      return;
    }
    openExportModal('all');
  };

  const handleExportTemplate = (template) => {
    openExportModal('single', template);
  };

  const handleExportCategory = () => {
    if (!activeCategory) {
      showToast('Please select a category first', 'info');
      return;
    }
    const categoryTemplates = templates.filter(t => (t.type || t.category || 'Uncategorized') === activeCategory);
    if (categoryTemplates.length === 0) {
      showToast('No templates in this category', 'info');
      return;
    }
    openExportModal('category');
  };

  const handleExportMostUsed = () => {
    const usedTemplates = templates.filter(t => (t.uses || 0) > 0);
    if (usedTemplates.length === 0) {
      showToast('No templates have been used yet', 'info');
      return;
    }
    openExportModal('most-used');
  };

  const handleExportSummary = () => {
    if (templates.length === 0) {
      showToast('No templates to export', 'error');
      return;
    }
    openExportModal('summary');
  };

  // ==================== TEMPLATE CRUD ====================

  const openCreateTemplate = () => {
    setTimeout(() => {
      if (createNameRef.current) createNameRef.current.value = '';
      if (createCategoryRef.current) createCategoryRef.current.value = '';
      if (createBodyRef.current) createBodyRef.current.value = '';
    }, 0);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><FilePlus2 size={16} style={{ verticalAlign: '-3px' }} /> Create New Template</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Template Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input 
                className="form-input" 
                ref={createNameRef}
                placeholder="e.g. Midweek Reminder"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select 
                className="form-select" 
                ref={createCategoryRef}
                defaultValue=""
              >
                <option value="">Select Category</option>
                <option value="Service">Service</option>
                <option value="Birthday">Birthday</option>
                <option value="Welcome">Welcome</option>
                <option value="Events">Events</option>
                <option value="Pastoral Care">Pastoral Care</option>
                <option value="Giving">Giving</option>
                <option value="Prayer">Prayer</option>
                <option value="Poll">Poll</option>
                <option value="Alert">Alert</option>
                <option value="Milestone">Milestone</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Message Body <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea 
              className="form-textarea" 
              rows="5" 
              ref={createBodyRef}
              placeholder="Type your template…"
            />
            <div className="form-hint">
              Use {'{FirstName}'}, {'{ChurchName}'}, {'{PastorName}'}, {'{Date}'}, {'{EventName}'}
            </div>
          </div>

          {/* Merge Fields Section */}
          <div className="form-group" style={{ marginTop: '12px' }}>
            <label className="form-label"><ClipboardList size={14} style={{ verticalAlign: '-2px' }} /> Available Merge Fields</label>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
              gap: '8px',
              maxHeight: '200px',
              overflowY: 'auto',
              padding: '8px',
              background: 'var(--cream)',
              borderRadius: '8px',
              border: '1px solid var(--border)'
            }}>
              {mergeFields.map(field => (
                <button
                  key={field.key}
                  className="btn btn-ghost btn-sm"
                  onClick={() => insertMergeField(field.key, createBodyRef)}
                  style={{
                    fontSize: '11px',
                    padding: '4px 8px',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    border: '1px solid transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--gold)';
                    e.currentTarget.style.background = 'var(--gold-pale)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'transparent';
                    e.currentTarget.style.background = 'transparent';
                  }}
                  title={`${field.label}: ${field.description} (e.g. ${field.example})`}
                >
                  <span style={{ fontWeight: '600', color: 'var(--navy)' }}>{field.key}</span>
                  <span style={{ fontSize: '9px', color: 'var(--text-light)', marginLeft: '4px' }}>
                    {field.label}
                  </span>
                </button>
              ))}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '6px' }}>
              <Lightbulb size={12} style={{ verticalAlign: '-2px' }} /> Click a merge field to insert it at the cursor position
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const name = createNameRef.current?.value?.trim() || '';
              const text = createBodyRef.current?.value?.trim() || '';
              const type = createCategoryRef.current?.value || 'custom';
              
              if (!name) {
                showToast('Please enter a template name', 'error');
                return;
              }
              if (!text) {
                showToast('Please enter template content', 'error');
                return;
              }
              
              setIsSubmitting(true);
              try {
                const result = await dispatch(createTemplate({ name, text, type }));
                if (result.payload?.success) {
                  closeModal();
                  await dispatch(getTemplates());
                  showToast(`📝 "${name}" saved!`, 'success');
                } else if (result.error) {
                  showToast(result.payload?.message || 'Failed to create template', 'error');
                }
              } catch (err) {
                showToast('Failed to create template', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Creating...</>
            ) : (
              'Save Template'
            )}
          </button>
        </div>
      </div>
    );
  };

  // Edit Template
  const handleEditTemplate = (template) => {
    setEditingTemplateId(template.id);
    
    setTimeout(() => {
      if (editNameRef.current) editNameRef.current.value = template.name || '';
      if (editCategoryRef.current) editCategoryRef.current.value = template.type || template.category || 'custom';
      if (editBodyRef.current) editBodyRef.current.value = template.text || template.body || '';
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit Template</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Template Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input 
                className="form-input" 
                ref={editNameRef}
                defaultValue=""
              />
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select 
                className="form-select"
                ref={editCategoryRef}
                defaultValue=""
              >
                {categories.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Message Body <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea 
              className="form-textarea" 
              rows="5"
              ref={editBodyRef}
              defaultValue=""
            />
            <div className="form-hint">
              Use {'{FirstName}'}, {'{ChurchName}'}, {'{PastorName}'}, {'{Date}'}, {'{EventName}'}
            </div>
          </div>

          {/* Merge Fields Section */}
          <div className="form-group" style={{ marginTop: '12px' }}>
            <label className="form-label"><ClipboardList size={14} style={{ verticalAlign: '-2px' }} /> Available Merge Fields</label>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
              gap: '8px',
              maxHeight: '200px',
              overflowY: 'auto',
              padding: '8px',
              background: 'var(--cream)',
              borderRadius: '8px',
              border: '1px solid var(--border)'
            }}>
              {mergeFields.map(field => (
                <button
                  key={field.key}
                  className="btn btn-ghost btn-sm"
                  onClick={() => insertMergeField(field.key, editBodyRef)}
                  style={{
                    fontSize: '11px',
                    padding: '4px 8px',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    border: '1px solid transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--gold)';
                    e.currentTarget.style.background = 'var(--gold-pale)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'transparent';
                    e.currentTarget.style.background = 'transparent';
                  }}
                  title={`${field.label}: ${field.description} (e.g. ${field.example})`}
                >
                  <span style={{ fontWeight: '600', color: 'var(--navy)' }}>{field.key}</span>
                  <span style={{ fontSize: '9px', color: 'var(--text-light)', marginLeft: '4px' }}>
                    {field.label}
                  </span>
                </button>
              ))}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '6px' }}>
              <Lightbulb size={12} style={{ verticalAlign: '-2px' }} /> Click a merge field to insert it at the cursor position
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const name = editNameRef.current?.value?.trim() || '';
              const body = editBodyRef.current?.value?.trim() || '';
              const category = editCategoryRef.current?.value || 'custom';
              
              if (!name) {
                showToast('Please enter a template name', 'error');
                return;
              }
              if (!body) {
                showToast('Please enter a message body', 'error');
                return;
              }
              
              setIsSubmitting(true);
              try {
                const result = await dispatch(updateTemplate({ 
                  id: template.id, 
                  data: { name, text: body, type: category } 
                }));
                if (result.payload?.success) {
                  closeModal();
                  await dispatch(getTemplates());
                  showToast(`✅ "${name}" updated successfully!`, 'success');
                } else if (result.error) {
                  showToast(result.payload?.message || 'Failed to update template', 'error');
                }
              } catch (err) {
                showToast('Failed to update template', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Saving...</>
            ) : (
              <><Save size={16} style={{ verticalAlign: '-3px' }} /> Save Changes</>
            )}
          </button>
        </div>
      </div>
    );
  };

  // Delete Template
  const handleDeleteTemplate = (template) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Delete Template</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete <strong>{template.name}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> This will permanently remove the template.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(deleteTemplate(template.id));
                if (result.payload?.success) {
                  closeModal();
                  await dispatch(getTemplates());
                  showToast(`🗑️ "${template.name}" deleted`, 'error');
                } else if (result.error) {
                  showToast(result.payload?.message || 'Failed to delete template', 'error');
                }
              } catch (err) {
                showToast('Failed to delete template', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Deleting...</>
            ) : (
              'Delete Template'
            )}
          </button>
        </div>
      </div>
    );
  };

  // Duplicate Template
  const handleDuplicateTemplate = (template) => {
    setIsSubmitting(true);
    dispatch(duplicateTemplate(template.id))
      .then(async (result) => {
        if (result.payload?.success) {
          await dispatch(getTemplates());
          showToast(`📋 "${template.name} (Copy)" created`, 'success');
        } else if (result.error) {
          showToast(result.payload?.message || 'Failed to duplicate template', 'error');
        }
      })
      .catch(() => {
        showToast('Failed to duplicate template', 'error');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  // Load Template to Broadcast
  const loadTemplateToBroadcast = (template) => {
    navigate('/broadcast');
    setTimeout(() => {
      const ta = document.getElementById('sms-body');
      if (ta) {
        ta.value = template.text || template.body || '';
        ta.dispatchEvent(new Event('input'));
      }
      showToast('📋 Template loaded!', 'success');
    }, 300);
  };

  // ==================== RENDER ====================

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
          <ClipboardList size={14} style={{ verticalAlign: '-2px' }} /> Pre-built and custom message templates for every church occasion.
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button 
            className="btn btn-ghost btn-sm" 
            onClick={handleExportSummary}
            title="Export templates summary"
            disabled={loading}
          >
            <ClipboardList size={16} style={{ verticalAlign: '-3px' }} /> Summary
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleExportMostUsed}
            title="Export most used templates"
            disabled={loading}
          >
            <TrendingUp size={16} style={{ verticalAlign: '-3px' }} /> Most Used
          </button>
          {activeCategory && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={handleExportCategory}
              title={`Export ${activeCategory} templates`}
              disabled={loading}
            >
              <Folder size={16} style={{ verticalAlign: '-3px' }} /> {activeCategory}
            </button>
          )}
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleExportAll}
            title="Export all templates"
            disabled={loading}
          >
            <Download size={16} style={{ verticalAlign: '-3px' }} /> Export All
          </button>
          <button className="btn btn-gold btn-sm" onClick={openCreateTemplate} disabled={loading || isSubmitting}>
            + New Template
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '20px', alignItems: 'center' }}>
        <div className="search-box" style={{ maxWidth: '300px', flex: '1' }}>
          <span className="search-icon"><Search size={16} /></span>
          <input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            title="Search by name, category, or content"
          />
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button 
            className={`btn ${activeCategory === '' ? 'btn-primary' : 'btn-ghost'} btn-sm`} 
            onClick={() => filterTemplates('')}
            disabled={loading}
          >
            All ({templates.length})
          </button>
          {categories.map(c => (
            <button 
              key={c} 
              className={`btn ${activeCategory === c ? 'btn-primary' : 'btn-ghost'} btn-sm`} 
              onClick={() => filterTemplates(c)}
              disabled={loading}
            >
              {c} ({templates.filter(t => (t.type || t.category || 'Uncategorized') === c).length})
            </button>
          ))}
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
          {filteredTemplates.length} templates found
        </div>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} />
          <div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading templates...</div>
        </div>
      ) : (
        <div id="templates-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px' }}>
          {currentItems.length > 0 ? (
            currentItems.map(t => {
              const category = t.type || t.category || 'Uncategorized';
              const color = getCategoryColor(category);
              const mergeFields = ((t.text || t.body || '').match(/\{[A-Za-z]+\}/g) || []);
              
              return (
                <div 
                  key={t.id} 
                  className="card" 
                  style={{ borderLeft: `3px solid ${color}` }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--navy)' }}>
                        {t.name || 'Unnamed Template'}
                      </div>
                      <span 
                        className="badge" 
                        style={{ 
                          background: `${color}18`, 
                          color: color, 
                          fontSize: '10px', 
                          marginTop: '4px' 
                        }}
                      >
                        {category}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                        Used {t.uses || 0}×
                      </div>
                      <button 
                        className="btn btn-ghost btn-sm btn-icon" 
                        onClick={() => handleExportTemplate(t)}
                        title={`📥 Export ${t.name}`}
                        disabled={loading}
                      >
                        <Download size={16} />
                      </button>
                      <button
                        className="btn btn-ghost btn-sm btn-icon"
                        onClick={() => handleEditTemplate(t)}
                        title={`✏️ Edit ${t.name}`}
                        disabled={loading}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        className="btn btn-ghost btn-sm btn-icon"
                        onClick={() => handleDuplicateTemplate(t)}
                        title={`📋 Duplicate ${t.name}`}
                        disabled={loading || isSubmitting}
                      >
                        {isSubmitting ? (
                          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                        ) : (
                          <Copy size={16} />
                        )}
                      </button>
                      <button
                        className="btn btn-ghost btn-sm btn-icon"
                        onClick={() => handleDeleteTemplate(t)}
                        title={`🗑️ Delete ${t.name}`}
                        style={{ color: 'var(--red)' }}
                        disabled={loading}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  
                  <div style={{ 
                    fontSize: '12px', 
                    color: 'var(--text-mid)', 
                    lineHeight: '1.6', 
                    background: 'var(--cream)', 
                    padding: '10px', 
                    borderRadius: '8px', 
                    marginBottom: '10px' 
                  }}>
                    {(t.text || t.body || 'No content')}
                  </div>
                  
                  {mergeFields.length > 0 && (
                    <div style={{ fontSize: '11px', marginBottom: '10px' }}>
                      <span style={{ color: 'var(--text-light)', marginRight: '4px' }}>Merge fields:</span>
                      {mergeFields.map(f => (
                        <code 
                          key={f} 
                          style={{ 
                            background: 'var(--navy)', 
                            color: 'var(--gold-light)', 
                            padding: '1px 5px', 
                            borderRadius: '3px', 
                            fontSize: '10px', 
                            marginRight: '4px' 
                          }}
                        >
                          {f}
                        </code>
                      ))}
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button 
                      className="btn btn-gold btn-sm" 
                      style={{ flex: 1 }} 
                      onClick={() => loadTemplateToBroadcast(t)}
                      title="Use this template in broadcast"
                      disabled={loading}
                    >
                      Use in Broadcast
                    </button>
                    <button 
                      className="btn btn-ghost btn-sm btn-icon" 
                      onClick={() => handleDuplicateTemplate(t)}
                      title={`📋 Duplicate ${t.name}`}
                      disabled={loading || isSubmitting}
                    >
                      {isSubmitting ? (
                        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      ) : (
                        <Copy size={16} />
                      )}
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <div style={{ gridColumn: '1 / -1' }}>
              <div className="empty-state">
                <div className="empty-icon"><ClipboardList size={40} strokeWidth={1.5} /></div>
                <div className="empty-title">No templates found</div>
                <div className="empty-text">Try adjusting your search or filters</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
          marginTop: '20px',
          padding: '16px',
          background: 'var(--white)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow)'
        }}>
          <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
            Showing <strong>{indexOfFirstItem + 1}</strong> to <strong>{Math.min(indexOfLastItem, totalItems)}</strong> of <strong>{totalItems}</strong> templates
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
              <select 
                className="form-select" 
                style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                title="Items per page"
                disabled={loading}
              >
                <option value={4}>4</option>
                <option value={6}>6</option>
                <option value={8}>8</option>
                <option value={12}>12</option>
              </select>
            </div>
            
            <div style={{ display: 'flex', gap: '4px' }}>
              <button 
                className="btn btn-ghost btn-sm"
                onClick={goToPreviousPage}
                disabled={currentPage === 1 || loading}
                style={{ 
                  opacity: currentPage === 1 ? 0.5 : 1, 
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer' 
                }}
                title="Previous page"
              >
                ← Prev
              </button>
              
              {getPageNumbers().map(page => (
                <button 
                  key={page}
                  className={`btn btn-sm ${page === currentPage ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => goToPage(page)}
                  title={`Go to page ${page}`}
                  disabled={loading}
                >
                  {page}
                </button>
              ))}
              
              <button 
                className="btn btn-ghost btn-sm"
                onClick={goToNextPage}
                disabled={currentPage === totalPages || loading}
                style={{ 
                  opacity: currentPage === totalPages ? 0.5 : 1, 
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' 
                }}
                title="Next page"
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (max-width: 1024px) {
          #templates-grid {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 768px) {
          #templates-grid {
            grid-template-columns: 1fr !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px;
          }
          .pagination-controls {
            flex-direction: column;
            align-items: center;
          }
          .page-numbers {
            flex-wrap: wrap;
            justify-content: center;
          }
        }
        @media (max-width: 480px) {
          .btn-sm {
            padding: 4px 8px;
            font-size: 11px;
          }
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .merge-fields-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: 6px;
        }
        .merge-field-btn {
          font-size: 11px;
          padding: 4px 8px;
          text-align: left;
          border: 1px solid var(--border);
          border-radius: 4px;
          background: var(--white);
          cursor: pointer;
          transition: all 0.2s;
        }
        .merge-field-btn:hover {
          border-color: var(--gold);
          background: var(--gold-pale);
        }
        .merge-field-key {
          font-weight: 600;
          color: var(--navy);
        }
        .merge-field-label {
          font-size: 9px;
          color: var(--text-light);
          margin-left: 4px;
        }
      `}</style>
    </>
  );
};

export default TemplatesLibrary;